/**
 * Currency minor-unit exponents and decimal-string <-> integer-minor-unit
 * conversion. Single source of truth for which currencies use 0, 2, or 3
 * decimal places — keep drizzle/0010_decimal_amounts_minor_units.sql's CASE
 * expression in sync with the two sets below if either ever changes.
 */
import { UserError } from "@/lib/user-error";


const ZERO_DECIMAL = new Set(["KRW", "JPY", "VND", "CLP", "ISK"]);
const THREE_DECIMAL = new Set(["BHD", "IQD", "JOD", "KWD", "LYD", "OMR", "TND"]);

/** Number of minor-unit decimal places for a currency code (case-insensitive). */
export function minorExponent(currency: string): number {
  const code = currency.toUpperCase();
  if (ZERO_DECIMAL.has(code)) return 0;
  if (THREE_DECIMAL.has(code)) return 3;
  return 2;
}

const AMOUNT_PATTERN = /^(\d+)(?:\.(\d+))?$/;

/** Postgres `integer` column max (2^31 − 1) — every amount column this parses into is `integer`, not bigint. */
const PG_INTEGER_MAX = 2147483647;

/**
 * Parses a user-entered decimal amount string into an integer minor-unit
 * amount, via string-based decimal shifting — never `Number(raw) * 10 **
 * exp`, which risks float drift (e.g. "1.15" * 100 === 114.99999999999999 in
 * IEEE754). Trims trailing zeros from the decimal part before checking it
 * against the currency's exponent, so "1.500" USD is accepted as 150.
 *
 * Throws (Korean message, matches assertValidAmount's wording) on: negative
 * amounts, non-numeric input, a decimal part longer than the currency's
 * exponent allows (e.g. "0.001" for USD, any decimals at all for KRW), or a
 * minor-unit result that wouldn't fit the destination `integer` column.
 */
export function parseAmountInput(raw: string, currency: string): number {
  const match = AMOUNT_PATTERN.exec(raw.trim());
  if (!match) throw new UserError("금액이 올바르지 않습니다");

  const [, intPart, fracPartRaw = ""] = match;
  const fracPart = fracPartRaw.replace(/0+$/, "");
  const exp = minorExponent(currency);
  if (fracPart.length > exp) throw new UserError("금액이 올바르지 않습니다");

  const minor = Number(intPart + fracPart.padEnd(exp, "0"));
  if (!Number.isSafeInteger(minor) || minor > PG_INTEGER_MAX) throw new UserError("금액이 올바르지 않습니다");
  return minor;
}

/**
 * Formats an integer minor-unit amount back into a decimal string (e.g. 150
 * USD -> "1.50", 10 USD -> "0.1"). Trailing zeros in the decimal part are
 * trimmed (and the decimal point dropped entirely when nothing is left) —
 * this is the single display rule every UI amount goes through, so "1.50"
 * shows as "1.5" and a whole-unit entry like "500.00" shows as just "500".
 */
export function formatAmount(amountMinor: number, currency: string): string {
  if (!Number.isInteger(amountMinor)) throw new UserError("금액이 올바르지 않습니다");
  const exp = minorExponent(currency);
  if (exp === 0) return String(amountMinor);

  const negative = amountMinor < 0;
  const digits = Math.abs(amountMinor).toString().padStart(exp + 1, "0");
  const intPart = digits.slice(0, -exp);
  const fracPart = digits.slice(-exp).replace(/0+$/, "");
  const sign = negative ? "-" : "";
  return fracPart ? `${sign}${intPart}.${fracPart}` : `${sign}${intPart}`;
}

/** Like parseAmountInput, but returns null instead of throwing — for live client-side preview/validation while the user is still typing. */
export function tryParseAmountInput(raw: string, currency: string): number | null {
  try {
    return parseAmountInput(raw, currency);
  } catch {
    return null;
  }
}

/**
 * Converts a minor-unit amount in `currency` into a minor-unit amount in
 * `baseCurrency`, given a whole-unit-to-whole-unit fx rate (fxRateToBase's
 * long-standing meaning: 1 whole unit of `currency` = `fxRateToBase` whole
 * units of `baseCurrency` — unchanged by this module). Corrects for the two
 * currencies potentially using different minor-unit exponents: the single
 * replacement for every prior `Math.round(amount * fx)` call site.
 */
export function toBaseMinor(
  amountMinor: number,
  currency: string,
  fxRateToBase: number | string,
  baseCurrency: string
): number {
  const fx = typeof fxRateToBase === "string" ? Number(fxRateToBase) : fxRateToBase;
  if (!Number.isFinite(fx) || fx <= 0) throw new UserError("환율이 올바르지 않습니다");
  const scale = Math.pow(10, minorExponent(baseCurrency) - minorExponent(currency));
  return Math.round(amountMinor * fx * scale);
}
